import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { ChangeEvent } from 'react';

const logger = LogManager.getLogger('aiVoices:AudioSamplePlayer');

// Reusable single-clip audio player: a play/pause control + scrubber + mm:ss
// readout over one <audio> element. Feature-neutral — the custom-phrase
// audition uses it now (PLAT-10504); the pre-saved sample listen reuses it
// (PLAT-10500). Driven entirely by the `url` prop; a new url resets playback.
interface AudioSamplePlayerProps {
  url: string;
  autoPlay?: boolean;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const AudioSamplePlayer = ({
  url,
  autoPlay = false,
}: AudioSamplePlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  // A fresh clip resets transport state; autoPlay kicks it off once loaded.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
    const audio = audioRef.current;
    if (audio && autoPlay) {
      audio.play().catch(err => {
        // Autoplay can be blocked by the browser — surface as "paused", not an
        // error (the user can press play).
        logger.debug('autoPlay blocked', err);
        setIsPlaying(false);
      });
    }
  }, [url, autoPlay]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => {
        logger.warn('play failed', err);
        setHasError(true);
      });
    }
  };

  const handleSeek = (e: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = Number(e.target.value);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  return (
    <Player>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
        onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setHasError(true)}
      />
      <button
        type="button"
        className="play"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        disabled={hasError}
      >
        {isPlaying ? '❚❚' : '▶'}
      </button>
      {hasError ? (
        <span className="error">Couldn’t load the audio.</span>
      ) : (
        <>
          <input
            type="range"
            className="scrubber"
            min={0}
            max={duration || 0}
            step={0.01}
            value={currentTime}
            onChange={handleSeek}
            aria-label="Seek"
          />
          <span className="time">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </>
      )}
    </Player>
  );
};

export default AudioSamplePlayer;

const Player = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);

  .play {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: var(--primary);
    color: var(--background);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    transition: var(--transition-normal);

    &:hover:not(:disabled) {
      filter: brightness(1.2) saturate(1.2);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .scrubber {
    flex: 1;
    min-width: 80px;
    accent-color: var(--primary);
    cursor: pointer;
  }

  .time {
    flex-shrink: 0;
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums;
  }

  .error {
    color: var(--destructive);
    font-size: var(--text-sm);
  }
`;
